import type { Standard, Standards } from '@ifs-gcp/core';
import type { Revision } from '@ifs-gcp/core/models';

import allFamilies from '@/assets/definitions/families/all.json';
import allModules from '@/assets/definitions/modules/all.json';

import type { VersionEntry } from '../types';

type FamilyKey = keyof typeof allFamilies;
export type ModuleKey = keyof typeof allModules;

const modulesToProcess = Object.keys(allModules).filter(
  (name) =>
    name.startsWith('deforestation_osa_scope_') || name.startsWith('ppwr_osa_private_label_'),
);

interface ModuleFile {
  id: string;
  type: 'standalone' | 'submodule';
  parent: string;
  family: string;
  licence: string;
  revision: Revision;
  schemaVersion: string;
  displayName: Record<string, string>;
  variables: Pick<
    Standard,
    | 'basicAuditorRoles'
    | 'basicAuditResults'
    | 'basicAuditTypes'
    | 'axpxGeneralSettings'
    | 'followUpResults'
    | 'improvementResults'
  > & {
    isConductible: boolean;
    general: {
      order: number;
      assetsPaths: {
        logoPath: string;
        familyLogoPath: string;
        result: Record<string, string>;
      };
    };
  };
  processes: Standard['processes'];
}

const svgImages = import.meta.glob<string>(
  [
    '@/assets/definitions/assets/images/deforestation/*.svg',
    '@/assets/definitions/assets/images/ppwr_osa_private_label/*.svg',
  ],
  {
    eager: true,
    query: '?url',
  },
);

const familyFiles = import.meta.glob<Standard['family']>(
  [
    '@/assets/definitions/families/deforestation_osa_*_1/*.json',
    '@/assets/definitions/families/ppwr_osa_private_label/*.json',
  ],
  {
    eager: true,
    query: '?json',
  },
);

const moduleFiles = import.meta.glob<ModuleFile>(
  [
    '@/assets/definitions/modules/deforestation_osa_scope_*_1/*.json',
    '@/assets/definitions/modules/ppwr_osa_private_label_*/*.json',
  ],
  {
    eager: true,
    query: '?json',
  },
);

const processModule = async (name: string, current: string): Promise<Standard> => {
  const module = moduleFiles[`/src/assets/definitions/modules/${name}/${current}.json`];
  const imagePath = `/src/assets/definitions/${module.variables?.general.assetsPaths?.logoPath}`;
  const isModuleConductible = Boolean(module?.variables?.isConductible);

  const moduleFamilyVersion = allFamilies[module.family as FamilyKey].current;
  const family =
    familyFiles[`/src/assets/definitions/families/${module.family}/${moduleFamilyVersion}.json`];

  return {
    id: module.id,
    family,
    type: module.type,
    label: module.displayName,
    licenceId: module.licence,
    image: svgImages[imagePath],
    version: module.revision,
    schemaVersion: module.schemaVersion,
    parent: module.parent,
    order: module?.variables?.general.order,
    isConductible: isModuleConductible,
    assetsPaths: module.variables.general.assetsPaths,
    processes: module.processes,
    basicAuditResults: module.variables.basicAuditResults,
    basicAuditorRoles: module.variables.basicAuditorRoles,
    basicAuditTypes: module.variables.basicAuditTypes,
    axpxGeneralSettings: module.variables.axpxGeneralSettings,
    followUpResults: module.variables.followUpResults,
    improvementResults: module.variables.improvementResults,
  };
};

export const loadAndProcessModules = async (): Promise<Standards> => {
  const moduleEntries = Object.entries(allModules) as [ModuleKey, VersionEntry][];

  const modules: Standards = {};

  for (const [moduleName, { current }] of moduleEntries) {
    if (modulesToProcess.includes(moduleName)) {
      modules[moduleName] = await processModule(moduleName, current);
    }
  }
  return modules;
};
